import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './IntegrationSelector.css'

type Language = 'pt' | 'en'

interface IntegrationOption {
    type: 'api' | 'webview'
    icon: string
    title: {
        pt: string
        en: string
    }
    description: {
        pt: string
        en: string
    }
    apiRequired: {
        pt: string
        en: string
    }
    features: {
        pt: string[]
        en: string[]
    }
    badge: {
        pt: string
        en: string
    }
}

const IntegrationSelector: React.FC = () => {
    const navigate = useNavigate()
    const [language, setLanguage] = useState<Language>('pt')

    const integrationOptions: IntegrationOption[] = [
        {
            type: 'webview',
            icon: '📱',
            title: {
                pt: 'Webview EmpreX',
                en: 'EmpreX Webview'
            },
            description: {
                pt: 'Use nosso front-end pronto. Integre em minutos com apenas a Admin API.',
                en: 'Use our ready-made front-end. Integrate in minutes with only the Admin API.'
            },
            apiRequired: {
                pt: '🔧 Implementar: Admin API apenas',
                en: '🔧 Implement: Admin API only'
            },
            features: {
                pt: [
                    'Implementação rápida',
                    'Pronto para produção',
                    'Manutenção zero',
                    'Customização de cores e logo'
                ],
                en: [
                    'Quick implementation',
                    'Production ready',
                    'Zero maintenance',
                    'Color and logo customization'
                ]
            },
            badge: {
                pt: 'Recomendado',
                en: 'Recommended'
            }
        },
        {
            type: 'api',
            icon: '🔌',
            title: {
                pt: 'API REST Completa',
                en: 'Full REST API'
            },
            description: {
                pt: 'Construa seu próprio front-end com controle total da experiência do usuário.',
                en: 'Build your own front-end with full control of the user experience.'
            },
            apiRequired: {
                pt: '🔧 Implementar: Admin API + Client API',
                en: '🔧 Implement: Admin API + Client API'
            },
            features: {
                pt: [
                    'Controle total do design e UX',
                    'Integração personalizada',
                    'Flexibilidade máxima',
                    'Acesso completo aos endpoints'
                ],
                en: [
                    'Full control of design and UX',
                    'Custom integration',
                    'Maximum flexibility',
                    'Complete endpoint access'
                ]
            },
            badge: {
                pt: 'Desenvolvimento Customizado',
                en: 'Custom Development'
            }
        }
    ]

    const handleSelectIntegration = (type: 'api' | 'webview') => {
        navigate(`/docs/${type}`)
    }

    return (
        <div className="integration-selector-container">
            {/* Language Toggle */}
            <div className="language-toggle-wrapper">
                <button
                    className={`language-button ${language === 'pt' ? 'active' : ''}`}
                    onClick={() => setLanguage('pt')}
                >
                    PT
                </button>
                <button
                    className={`language-button ${language === 'en' ? 'active' : ''}`}
                    onClick={() => setLanguage('en')}
                >
                    EN
                </button>
            </div>

            <div className="integration-selector-header">
                <h1 className="integration-selector-title">
                    {language === 'pt' ? 'Bem-vindo à EmpreX' : 'Welcome to EmpreX'}
                </h1>
                <div className="integration-intro">
                    <p className="integration-intro-text">
                        {language === 'pt'
                            ? 'A solução de crédito embutida para o seu negócio: aumente sua receita e engaje ainda mais seus usuários oferecendo empréstimos diretos a colaboradores e parceiros. Com uma infraestrutura robusta e fácil de integrar, a EmpreX gerencia todo o ciclo do crédito — da originação ao pagamento — de forma segura, automatizada e totalmente conectada à sua operação.'
                            : 'The embedded credit solution for your business: increase your revenue and engage your users even more by offering direct loans to employees and partners. With a robust and easy-to-integrate infrastructure, EmpreX manages the entire credit cycle — from origination to payment — in a secure, automated way, fully connected to your operation.'
                        }
                    </p>
                </div>
                <h2 className="integration-selector-subtitle-main">
                    {language === 'pt' ? 'Escolha seu Tipo de Integração' : 'Choose Your Integration Type'}
                </h2>
                <p className="integration-selector-subtitle">
                    {language === 'pt'
                        ? 'Existem duas formas de integrar com as APIs da EmpreX, dependendo da sua preferência de interface:'
                        : 'There are two ways to integrate with the EmpreX APIs, depending on your preferred user interface setup:'
                    }
                </p>
            </div>

            <div className="integration-options">
                {integrationOptions.map((option) => (
                    <div
                        key={option.type}
                        className={`integration-option ${option.type === 'webview' ? 'recommended' : ''}`}
                        onClick={() => handleSelectIntegration(option.type)}
                    >
                        {option.type === 'webview' && (
                            <div className="integration-badge">
                                {option.badge[language]}
                            </div>
                        )}

                        <div className="integration-icon">{option.icon}</div>

                        <h2 className="integration-title">
                            {option.title[language]}
                        </h2>

                        <p className="integration-description">
                            {option.description[language]}
                        </p>

                        <div className="integration-api-required">
                            {option.apiRequired[language]}
                        </div>

                        <div className="integration-features">
                            <h3 className="features-title">
                                {language === 'pt' ? 'Principais recursos:' : 'Key features:'}
                            </h3>
                            <ul className="features-list">
                                {option.features[language].map((feature, index) => (
                                    <li key={index}>
                                        <span className="feature-check">✓</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <button className="integration-button">
                            {language === 'pt' ? 'Ver Documentação' : 'View Documentation'}
                            <span className="button-arrow">→</span>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default IntegrationSelector
